import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/lib/i18n";
import AppHeader from "@/components/AppHeader";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      toast.success(`${t("cd.welcome")} ${res.user.name || res.user.email}`);
      navigate(
        res.user.role === "admin" || res.user.role === "operator"
          ? "/admin"
          : "/dashboard"
      );
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen bg-[#030303] text-white">
      <AppHeader />
      <main className="max-w-md mx-auto px-6 py-20">
        <p className="text-xs font-mono uppercase tracking-widest text-[#FFD700] mb-3">
          {t("login.tag")}
        </p>
        <h1 className="font-display text-5xl font-black tracking-tighter mb-2">
          {t("login.title")}
        </h1>
        <p className="text-white/50 mb-10">{t("login.subtitle")}</p>

        <form onSubmit={onSubmit} className="space-y-5">
          <div>
            <label className="text-xs uppercase tracking-widest text-white/60 font-mono block mb-2">
              {t("field.email")}
            </label>
            <input
              data-testid="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full bg-transparent border border-white/20 focus:border-white focus:ring-1 focus:ring-white outline-none px-3 py-3 text-sm font-mono"
              placeholder="you@domain.com"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/60 font-mono block mb-2">
              {t("field.password")}
            </label>
            <input
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full bg-transparent border border-white/20 focus:border-white focus:ring-1 focus:ring-white outline-none px-3 py-3 text-sm font-mono"
              placeholder="••••••••"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="login-submit-btn"
            className="w-full bg-[#FFD700] hover:bg-[#F4C430] disabled:opacity-50 text-black font-semibold py-3 flex items-center justify-center gap-2 transition-colors"
          >
            {submitting ? t("login.submitting") : t("login.submit")}
            <ArrowRight className="w-4 h-4" />
          </button>
        </form>

        <p className="text-sm text-white/50 mt-8 text-center">
          {t("login.noAccount")}{" "}
          <Link
            to="/register"
            data-testid="login-register-link"
            className="text-[#FFD700] hover:underline"
          >
            {t("login.signupLink")}
          </Link>
        </p>
      </main>
    </div>
  );
}
